import { Td, Tr, Flex, Text } from '@chakra-ui/react';
import { getIn } from 'formik';
import React, { useState } from 'react';

import { recurringDate } from '@finley-technologies/finley-date';

import { RecurringDateConfigView } from '../../property/RecurringDateConfigView';
import { IntermediateFieldsView } from './IntermediateFieldView';
import {
  ChevronIconButton,
  DeleteIconButton,
  SaveIconButton,
} from 'packages/common-frontend/components/IconButton';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { WipConfig } from 'packages/creditAgreements/ui/types';

const LiabilityAccountConfig = ({
  _config_,
  wipConfig,
  index,
  fullPropertyKey,
  columnCount,
}: {
  _config_: CreditAgreement.Config.Liabilities.Inputs.Config;
  wipConfig?: WipConfig;
  index: number;
  fullPropertyKey: string;
  columnCount: number;
}) => {
  const [isConfigOpen, setIsConfigOpen] = useState(false);

  const deleteLiabilityAccountConfig = (): void => {
    if (!wipConfig?.isMutateAllowed(fullPropertyKey)) return;

    const liabilityAccount = getIn(wipConfig.formProps.values, fullPropertyKey);
    const existingConfigurations =
      liabilityAccount._config_?.inputConfigurations ?? [];
    existingConfigurations.splice(index, 1);

    wipConfig.formProps.setFieldValue(fullPropertyKey, {
      ...liabilityAccount,
      _config_: { inputConfigurations: existingConfigurations },
    });

    wipConfig.saveCreditAgreementFullArtifactWip({
      variables: {
        creditAgreementFullArtifactWip: {
          id: wipConfig.formProps.values.id,
          creditFacilityId: wipConfig.formProps.values.creditFacilityId,
          creditAgreement: JSON.stringify(wipConfig.formProps.values),
        },
      },
    });
  };

  const dates = recurringDate.createDateListFromConfig(_config_.accrualDates);

  return (
    <>
      <Tr>
        <Td colSpan={columnCount - 1}>
          {`Configuration #${index + 1}: `}
          {dates.length
            ? `effective from ${dates[0]} through ${dates[dates.length - 1]}`
            : 'accrual schedule not yet configured'}
        </Td>
        <Td colSpan={1}>
          <ChevronIconButton
            openState={isConfigOpen}
            setOpenState={setIsConfigOpen}
            boxSize="5"
          />
        </Td>
      </Tr>

      {isConfigOpen && (
        <Tr>
          <Td colSpan={columnCount}>
            <Flex flexDir="column">
              <Flex alignItems="center" mb="3">
                <Text fontSize="large" fontWeight="bold" mr="2">
                  Config
                </Text>
                {wipConfig?.isMutateAllowed(fullPropertyKey) && (
                  <>
                    <DeleteIconButton
                      ariaLabel="delete config"
                      label="Delete Configuration"
                      onClick={() => {
                        deleteLiabilityAccountConfig();
                      }}
                    />
                    <SaveIconButton
                      ariaLabel="save config"
                      label="Save Configuration"
                      onClick={() => {
                        wipConfig.saveCreditAgreementFullArtifactWip({
                          variables: {
                            creditAgreementFullArtifactWip: {
                              id: wipConfig.formProps.values.creditFacilityId,
                              creditFacilityId:
                                wipConfig.formProps.values.creditFacilityId,
                              creditAgreement: JSON.stringify(
                                wipConfig.formProps.values,
                              ),
                            },
                          },
                        });
                      }}
                      isLoading={
                        wipConfig.isSaveCreditAgreementFullArtifactWipLoading
                      }
                    />
                    {/* TODO: @sachinr4 - QA placeholder */}
                  </>
                )}
              </Flex>
              <RecurringDateConfigView
                recurringDateConfig={_config_.accrualDates}
                wipConfig={wipConfig}
                fullPropertyKey={`${fullPropertyKey}._config_.inputConfigurations[${index}].accrualDates`}
                displayName="Accrual Schedule"
                enableEdit
              />
              {/* TODO: @sachinr4 - Data accessors placeholder */}
              <IntermediateFieldsView
                wipConfig={wipConfig}
                intermediateFields={_config_.intermediateFields}
                configPropertyKey={`${fullPropertyKey}._config_.inputConfigurations[${index}]`}
                basePropertyKey={`${fullPropertyKey}._config_.inputConfigurations[${index}].intermediateFields`}
              />
            </Flex>
          </Td>
        </Tr>
      )}
    </>
  );
};

export const LiabilityAccountConfigurationRows = ({
  _config_,
  wipConfig,
  fullPropertyKey,
  columnCount,
}: {
  _config_: CreditAgreement.Config.Liabilities.Config;
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  columnCount: number;
}) => {
  const inputConfigurations = _config_?.inputConfigurations ?? [];

  if (inputConfigurations.length === 0) {
    return (
      <Tr>
        <Td colSpan={columnCount}>
          <Text>
            No configuration is available, please add a new configuration.{' '}
          </Text>
        </Td>
      </Tr>
    );
  }

  return (
    <>
      {inputConfigurations.map((inputConfiguration, index) => {
        return (
          <LiabilityAccountConfig
            // eslint-disable-next-line react/no-array-index-key
            key={`${fullPropertyKey}[${index}]`}
            _config_={inputConfiguration}
            index={index}
            wipConfig={wipConfig}
            fullPropertyKey={fullPropertyKey}
            columnCount={columnCount}
          />
        );
      })}
    </>
  );
};
